<script setup lang="ts">
import {ref} from "vue";
import SearchInput from "@/core/components/search-bar/SearchInput.vue";
import {drugInteractionApi} from "@/container";
import Icon from "@/core/components/icon/Icon.vue";
import {Badge} from "@/core/components/ui/badge";
import {VidalPathology} from "@/customer/interfaces/Patient";

const pathologies = defineModel<Omit<VidalPathology, 'id'>[]>('pathologies', {required: true});

const pathologiesLoading = ref(false);
const pathologiesSearchQuery = ref('');
const searchResults = ref<Omit<VidalPathology, 'id'>[]>();
let timer: NodeJS.Timeout;

/**
 * Search vidal pathologies.
 * @param query
 */
const search = (query: string) => {
    pathologiesSearchQuery.value = query;
    if (timer) {
        clearTimeout(timer);
    }

    if (query.length === 0 || query.length < 3) {
        searchResults.value = undefined;
    } else {
        timer = setTimeout(async () => {
            pathologiesLoading.value = true;
            await drugInteractionApi().pathologies(query)
                .then(response => {
                    searchResults.value = response;
                })
                .finally(() => pathologiesLoading.value = false);
        }, 500);
    }
};

/**
 * Update selected pathologies.
 * @param pathology
 */
const updatePathologies = (pathology: Omit<VidalPathology, 'id'>) => {
    const pathologyExists = pathologies.value.find(selected => selected.vidal_id === pathology.vidal_id);

    if (!pathologyExists) {
        pathologies.value.push(pathology);
    } else {
        const indexToRemove = pathologies.value.indexOf(pathologyExists);
        if (indexToRemove !== -1) {
            pathologies.value.splice(indexToRemove, 1);
        }
    }

    pathologiesSearchQuery.value = '';
    searchResults.value = undefined;
};
</script>

<template>
  <div class="relative">
    <search-input
      class="w-full form-input"
      :is-loading="pathologiesLoading"
      :model-value="pathologiesSearchQuery"
      placeholder="Rechercher des pathologies..."
      @input="search"
    />
    <div
      v-if="pathologiesLoading || searchResults"
      class="absolute top-full left-0 right-0 z-50 mt-1 shadow-lg border bg-white"
    >
      <div class="p-2">
        <!-- Loading State -->
        <div v-if="pathologiesLoading" class="flex items-center justify-center py-4">
          <div class="flex items-center space-x-2 text-muted-foreground">
            <span class="text-sm">Recherche en cours...</span>
          </div>
        </div>

        <!-- Results -->
        <div v-else-if="searchResults" class="max-h-40 overflow-x-clip overflow-y-scroll">
          <div v-if="searchResults.length > 0" class="space-y-1">
            <div
              v-for="pathology in searchResults"
              :key="pathology.vidal_id"
              class="w-full justify-start h-auto p-3 text-left cursor-pointer hover:bg-gray-100 text-sm"
              @click="updatePathologies(pathology)"
            >
              {{ pathology.title }}
            </div>
          </div>

          <div v-else class="py-4 text-center">
            <p class="text-sm text-muted-foreground">
              Aucun résultat trouvé
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="pathologies.length > 0">
      <div class="h-px bg-gray-300 my-2" />
      <div class="space-y-3">
        <div class="flex flex-wrap gap-2">
          <Badge
            v-for="pathology in pathologies"
            :key="pathology.vidal_id"
            variant="secondary"
            class="px-3 py-1 cursor-pointer flex items-center"
            @click="updatePathologies(pathology)"
          >
            <span>{{ pathology.title }}</span>
            <Icon
              name="mdi-close"
              :size="15"
              class="ml-1 h-2 w-2 group-hover:text-destructive-foreground"
            />
          </Badge>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.form-input) {
    @apply focus:ring-primary
}
</style>
